import { useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import {
  useSettings,
  DEFAULT_HERO_BANNER,
  DEFAULT_SALE_TIMER,
} from '@/context/SettingsContext';
import ImageInputField from '@/components/admin/ImageInputField';
import HeroBanner from '@/components/shop/HeroBanner';
import SaleTimer from '@/components/shop/SaleTimer';
import { Eye, ArrowSquareOut, Trash } from '@phosphor-icons/react';

const TABS = [
  { id: 'banner', label: 'Banner Manager' },
  { id: 'timer', label: 'Sale Timer' },
  { id: 'featured', label: 'Featured Products' },
  { id: 'preview', label: 'Homepage Preview' },
];

const PRESETS = [
  { label: '1 Day', days: 1 },
  { label: '2 Days', days: 2 },
  { label: '3 Days', days: 3 },
  { label: '7 Days', days: 7 },
];

const toLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const tzOff = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - tzOff).toISOString().slice(0, 16);
};
const toIso = (local) => (local ? new Date(local).toISOString() : '');

export default function AdminHomeManager() {
  const { settings, refresh } = useSettings();
  const [tab, setTab] = useState('banner');
  const [banner, setBanner] = useState(DEFAULT_HERO_BANNER);
  const [timer, setTimer] = useState(DEFAULT_SALE_TIMER);
  const [featured, setFeatured] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setBanner({ ...DEFAULT_HERO_BANNER, ...(settings?.hero_banner || {}) });
    setTimer({ ...DEFAULT_SALE_TIMER, ...(settings?.sale_timer || {}) });
  }, [settings?.hero_banner, settings?.sale_timer]);

  useEffect(() => {
    api.get('/products', { params: { limit: 8, featured: true, active_only: false } })
      .then((r) => setFeatured(r.data || [])).catch(() => setFeatured([]));
  }, [settings?.content_version]);

  const bannerHasChanges = useMemo(() => JSON.stringify(banner) !== JSON.stringify({ ...DEFAULT_HERO_BANNER, ...(settings?.hero_banner || {}) }), [banner, settings]);
  const timerHasChanges = useMemo(() => JSON.stringify(timer) !== JSON.stringify({ ...DEFAULT_SALE_TIMER, ...(settings?.sale_timer || {}) }), [timer, settings]);

  const saveBanner = async () => {
    setBusy(true);
    try {
      await api.put('/settings', { hero_banner: banner });
      await refresh();
      toast.success('Banner saved — live on homepage');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  const clearBannerImage = () => {
    setBanner((b) => ({ ...b, image_url: '', image_public_id: '' }));
  };

  const saveTimer = async () => {
    if (timer.enabled && !timer.end_iso) {
      toast.error('Set an end date/time or use a quick preset');
      return;
    }
    setBusy(true);
    try {
      await api.put('/settings', { sale_timer: timer });
      await refresh();
      toast.success('Timer saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  const stopTimer = async () => {
    setBusy(true);
    try {
      const next = { ...timer, enabled: false };
      await api.put('/settings', { sale_timer: next });
      setTimer(next);
      await refresh();
      toast.success('Timer stopped');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };

  const applyPreset = (days) => {
    const start = new Date();
    const end = new Date(start.getTime() + days * 24 * 60 * 60 * 1000);
    setTimer((t) => ({ ...t, enabled: true, start_iso: start.toISOString(), end_iso: end.toISOString() }));
  };

  const inputCls = 'w-full h-11 px-3 rounded-xl bg-white border border-stone-200 focus:border-olive-400 focus:outline-none text-sm';

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-heading text-3xl text-stone-900">Home Page Manager</h1>
        <Link to="/" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-olive-700 hover:text-olive-800">
          <ArrowSquareOut size={16}/> Open live homepage
        </Link>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-stone-200">
        {TABS.map((t) => (
          <button
            key={t.id}
            data-testid={`home-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 ${tab === t.id ? 'border-terracotta-600 text-terracotta-700' : 'border-transparent text-stone-500 hover:text-stone-700'}`}
          >{t.label}</button>
        ))}
      </div>

      {tab === 'banner' && (
        <section data-testid="banner-manager" className="bg-white rounded-2xl border border-stone-200 p-5 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-heading text-lg text-stone-900">Hero Banner</h2>
              <p className="text-xs text-stone-500">Single active banner. Supports JPG, PNG, WEBP, screenshots. Auto-responsive.</p>
            </div>
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                data-testid="banner-enabled"
                checked={!!banner.enabled}
                onChange={(e) => setBanner({ ...banner, enabled: e.target.checked })}
              />
              Enable banner
            </label>
          </div>

          <ImageInputField
            label="Banner image (any size)"
            value={banner.image_url}
            onChange={(e) => setBanner({ ...banner, image_url: e.target.value })}
            onUpload={({ url, public_id }) => setBanner((b) => ({ ...b, image_url: url, image_public_id: public_id }))}
            folder="hero_banner/"
            placeholder="https://..."
            testIdInput="banner-image-url"
            testIdUrlTab="banner-image-url-tab"
            testIdUploadTab="banner-image-upload-tab"
          />

          {banner.image_url && (
            <div className="relative">
              <img src={banner.image_url} alt="Preview" data-testid="banner-preview-img" className="w-full max-h-72 object-cover rounded-xl border border-stone-200"/>
              <button type="button" onClick={clearBannerImage} data-testid="banner-clear-image" className="absolute top-2 right-2 inline-flex items-center gap-1 h-8 px-3 rounded-full bg-white/90 hover:bg-white text-red-700 text-xs font-semibold shadow"><Trash size={14}/> Remove</button>
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-stone-600">Title (optional)</label>
              <input data-testid="banner-title" className={inputCls} value={banner.title} onChange={(e) => setBanner({ ...banner, title: e.target.value })} placeholder="Fresh groceries in 30 mins"/>
            </div>
            <div>
              <label className="text-xs font-medium text-stone-600">Subtitle (optional)</label>
              <input data-testid="banner-subtitle" className={inputCls} value={banner.subtitle} onChange={(e) => setBanner({ ...banner, subtitle: e.target.value })} placeholder="Free delivery above ₹499"/>
            </div>
            <div>
              <label className="text-xs font-medium text-stone-600">Button text (optional)</label>
              <input data-testid="banner-button-text" className={inputCls} value={banner.button_text} onChange={(e) => setBanner({ ...banner, button_text: e.target.value })} placeholder="Shop now"/>
            </div>
            <div>
              <label className="text-xs font-medium text-stone-600">Button link (optional)</label>
              <input data-testid="banner-button-link" className={inputCls} value={banner.button_link} onChange={(e) => setBanner({ ...banner, button_link: e.target.value })} placeholder="/category/staples or https://..."/>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-xs text-stone-500 inline-flex items-center gap-1"><Eye size={14}/> Live preview below reflects your changes.</div>
            <button type="button" data-testid="banner-save" onClick={saveBanner} disabled={busy || !bannerHasChanges} className="h-10 px-6 rounded-full bg-terracotta-600 text-white font-semibold hover:bg-terracotta-700 disabled:bg-stone-300">{busy ? 'Saving…' : 'Save banner'}</button>
          </div>

          {banner.enabled && banner.image_url && (
            <div className="pt-3 border-t border-stone-100">
              <p className="text-xs uppercase tracking-wider text-stone-500 mb-2">Live preview</p>
              <PreviewFrame>
                <HeroBanner />
              </PreviewFrame>
            </div>
          )}
        </section>
      )}

      {tab === 'timer' && (
        <section data-testid="sale-timer-manager" className="bg-white rounded-2xl border border-stone-200 p-5 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-heading text-lg text-stone-900">Sale Countdown Timer</h2>
              <p className="text-xs text-stone-500">Auto-hides on expiry. Live countdown — no refresh needed.</p>
            </div>
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" data-testid="timer-enabled" checked={!!timer.enabled} onChange={(e) => setTimer({ ...timer, enabled: e.target.checked })}/>
              Enable timer
            </label>
          </div>

          <div>
            <label className="text-xs font-medium text-stone-600">Title</label>
            <input data-testid="timer-title" className={inputCls} value={timer.title} onChange={(e) => setTimer({ ...timer, title: e.target.value })} placeholder="Sale ends in"/>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-stone-600">Start (optional)</label>
              <input data-testid="timer-start" type="datetime-local" className={inputCls} value={toLocalInput(timer.start_iso)} onChange={(e) => setTimer({ ...timer, start_iso: toIso(e.target.value) })}/>
            </div>
            <div>
              <label className="text-xs font-medium text-stone-600">End</label>
              <input data-testid="timer-end" type="datetime-local" className={inputCls} value={toLocalInput(timer.end_iso)} onChange={(e) => setTimer({ ...timer, end_iso: toIso(e.target.value) })}/>
            </div>
          </div>

          <div>
            <p className="text-xs font-medium text-stone-600 mb-2">Quick presets (from now):</p>
            <div className="flex flex-wrap gap-2">
              {PRESETS.map((p) => (
                <button key={p.days} type="button" data-testid={`timer-preset-${p.days}d`} onClick={() => applyPreset(p.days)} className="h-9 px-4 rounded-full border border-stone-300 text-sm hover:border-terracotta-500 hover:text-terracotta-700">{p.label}</button>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap gap-2 justify-end">
            <button type="button" data-testid="timer-stop" onClick={stopTimer} disabled={busy || !timer.enabled} className="h-10 px-5 rounded-full border border-stone-300 disabled:opacity-50">Stop timer</button>
            <button type="button" data-testid="timer-save" onClick={saveTimer} disabled={busy || !timerHasChanges} className="h-10 px-6 rounded-full bg-terracotta-600 text-white font-semibold hover:bg-terracotta-700 disabled:bg-stone-300">{busy ? 'Saving…' : 'Save timer'}</button>
          </div>

          {timer.enabled && timer.end_iso && (
            <div className="pt-3 border-t border-stone-100">
              <p className="text-xs uppercase tracking-wider text-stone-500 mb-2">Live preview</p>
              <PreviewFrame>
                <SaleTimer />
              </PreviewFrame>
            </div>
          )}
        </section>
      )}

      {tab === 'featured' && (
        <section data-testid="featured-products" className="bg-white rounded-2xl border border-stone-200 p-5 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-lg text-stone-900">Featured Products</h2>
            <Link to="/admin/products" className="text-sm text-olive-700 hover:text-olive-800">Manage products →</Link>
          </div>
          <p className="text-xs text-stone-500">Mark products as <span className="font-semibold">Featured</span> or set a low <span className="font-semibold">Priority</span> number in the product editor to control the homepage order.</p>
          {featured.length === 0 ? (
            <p className="text-sm text-stone-500">No featured products yet.</p>
          ) : (
            <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {featured.map((p) => (
                <li key={p.id} data-testid={`featured-item-${p.id}`} className="flex items-center gap-3 bg-stone-50 rounded-xl p-2 border border-stone-100">
                  <img src={p.image_url || '/placeholder.png'} alt={p.name} className="w-14 h-14 rounded-lg object-cover"/>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-stone-900 truncate">{p.name}</p>
                    <p className="text-xs text-stone-500">Priority {p.priority || 0} · ₹{p.price}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {tab === 'preview' && (
        <section data-testid="homepage-preview" className="bg-white rounded-2xl border border-stone-200 p-5 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-lg text-stone-900">Homepage Preview</h2>
            <Link to="/" target="_blank" rel="noreferrer" className="text-sm text-olive-700 hover:text-olive-800 inline-flex items-center gap-1"><ArrowSquareOut size={14}/> Open homepage</Link>
          </div>
          <PreviewFrame>
            <div className="space-y-4">
              <HeroBanner />
              <SaleTimer />
            </div>
          </PreviewFrame>
        </section>
      )}
    </div>
  );
}

function PreviewFrame({ children }) {
  return (
    <div className="rounded-2xl border border-stone-200 bg-stone-50 p-4">
      <div className="bg-white rounded-xl p-3">{children}</div>
    </div>
  );
}
